/**
 * @fileoverview User Management Page
 *
 * Purpose: List, search, and filter all platform users.
 * URL: /admin/users
 * Protection: Requires super_admin role
 *
 * Features:
 * - Search by name or email with debounce
 * - Filter by system role
 * - Data table with user details
 * - Link to user detail page
 *
 * Dependencies:
 * - Convex: admin.users.list
 * - shadcn Table, Badge, Button, Input, Select
 */

import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/ui/composed/data-display/table";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import { UserAvatar } from "@/ui/composed/user-avatar";
import { Search, ChevronRight } from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import type { SystemRole } from "@cvx/users/schema";
import type { Id } from "@cvx/_generated/dataModel";

/** Shape of a user row from the admin.users.list query */
interface AdminUserRow {
  _id: Id<"users">;
  _creationTime: number;
  email: string;
  firstName?: string;
  lastName?: string;
  systemRole?: string;
  suspended?: boolean;
  avatarUrl?: string;
  orgCount: number;
}

export const Route = createFileRoute("/_app/_auth/admin/_layout/users")({
  beforeLoad: async ({ context }) => {
    const user = await context.queryClient.ensureQueryData(
      convexQuery(api.users.queries.getCurrentUser, {}),
    );
    if (!user?.isOwner) {
      throw redirect({ to: "/admin/master-library" });
    }
  },
  component: UsersPage,
});

// ---------------------------------------------------------------------------
// Role badge styling
// ---------------------------------------------------------------------------

const ROLE_BADGE_VARIANT: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  super_admin: "default",
  user: "secondary",
};

const ROLE_LABELS: Record<string, string> = {
  super_admin: "Super Admin",
  user: "User",
};

// ---------------------------------------------------------------------------
// Role filter options
// ---------------------------------------------------------------------------

type RoleFilter = "all" | "super_admin" | "user";

const ROLE_FILTERS: { label: string; value: RoleFilter }[] = [
  { label: "All Roles", value: "all" },
  { label: "Super Admin", value: "super_admin" },
  { label: "User", value: "user" },
];

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

function UsersPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState<RoleFilter>("all");

  // Debounce search input
  const debouncedSearch = useDebounce(search, 300);

  const queryArgs = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      systemRole: roleFilter === "all" ? undefined : (roleFilter as SystemRole),
    }),
    [debouncedSearch, roleFilter],
  );

  const { data: users } = useQuery(
    convexQuery(api.admin.users.list, queryArgs),
  );

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">Users</h1>
        <p className="text-sm text-muted-foreground">
          Manage platform users, roles, and access.
        </p>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-1 rounded-lg border p-1">
          {ROLE_FILTERS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setRoleFilter(filter.value)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                roleFilter === filter.value
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Organisations</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users && users.length > 0 ? (
              users.map((user: AdminUserRow) => (
                <TableRow
                  key={user._id}
                  className="cursor-pointer"
                  onClick={() =>
                    navigate({
                      to: "/admin/users/$userId",
                      params: { userId: user._id },
                    })
                  }
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserAvatar
                        avatarUrl={user.avatarUrl}
                        firstName={user.firstName}
                        lastName={user.lastName}
                        email={user.email}
                        className="h-8 w-8"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">
                          {[user.firstName, user.lastName]
                            .filter(Boolean)
                            .join(" ") || user.email}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        ROLE_BADGE_VARIANT[user.systemRole ?? "user"] ??
                        "secondary"
                      }
                    >
                      {ROLE_LABELS[user.systemRole ?? "user"] ?? "User"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {user.suspended ? (
                      <Badge variant="destructive">Suspended</Badge>
                    ) : (
                      <Badge variant="outline">Active</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {user.orgCount}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(user._creationTime).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm">
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={6}
                  className="py-8 text-center text-muted-foreground"
                >
                  {users === undefined ? "Loading..." : "No users found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Simple debounce hook for search input.
 */
function useDebounce(value: string, delay: number): string {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}
